import { flushSync } from "react-dom";
import type { Client, Opened } from "../../client/index.ts";
import type { Match, Navigator, NavKind, Routes } from "./router.ts";
import { flow, reducedMotion, shoot, visible, windows } from "./zoom.ts";

export interface Route {
  readonly url: string;
  readonly match?: Match;
  readonly opened?: Opened;
}

export interface Level {
  readonly id: number;
  readonly route: Route;
}

interface Zoom {
  readonly url: string;
  seek(progress: number): void;
  land(): void;
  cancel(): void;
  settle(): void;
}

export interface Stage {
  readonly client: Client;
  readonly routes: Routes;
  readonly navigator: Navigator;
  shown: { readonly here: Level; readonly other?: Level };
  ids: number;
  latest: number;
  flying: Zoom | null;
  owed: (() => void) | null;
  readonly listeners: Set<() => void>;
}

export function open(routes: Routes, url: string): Route {
  const match = routes.match(url);
  return match ? { url, match, opened: match.entry.open(match.params, match.query) } : { url };
}

export const pathOf = (url: string) => new URL(url, location.origin).pathname;
const stageElement = () => document.querySelector<HTMLElement>("[data-palco]");
export const levelElement = (id: number) => document.querySelector<HTMLElement>(`[data-nivel="${id}"]`);

export function show(stage: Stage, shown: Stage["shown"]): void {
  flushSync(() => {
    stage.shown = shown;
    for (const listener of stage.listeners) listener();
  });
}

export function toHash(within: ParentNode, hash: string): boolean {
  const target = within.querySelector(`[id="${CSS.escape(hash.slice(1))}"]`);
  target?.scrollIntoView();
  return target !== null;
}

function cardOf(level: Element | null, place: Route, link?: Element | null): Element | null {
  const mark = `[data-zoom-origin="${CSS.escape(place.opened?.morph ?? "")}"]`;
  if (link) return link.closest(mark) ?? link;
  return level?.querySelector(mark) ?? level?.querySelector(`a[href="${CSS.escape(pathOf(place.url))}"]`) ?? null;
}

const fieldAt = (level: Element | null, hash: string) =>
  (hash === "" ? null : level?.querySelector<HTMLElement>(`[id="${CSS.escape(hash.slice(1))}"][data-foco] textarea`) ?? null);

export function focus(stage: Stage, kind: NavKind, back: boolean, leaving: Route, hash = ""): void {
  const token = stage.latest;
  const field = () => fieldAt(levelElement(stage.shown.here.id), hash);
  const place = (): HTMLElement | null => {
    const active = document.activeElement;
    if (active && active !== document.body && !stageElement()?.contains(active)) return null;
    const level = levelElement(stage.shown.here.id);
    const control = kind === "sibling" ? level?.querySelector<HTMLElement>(`[data-irmao="${back ? "prev" : "next"}"]`) : null;
    const card = kind === "zoom-out" || back ? cardOf(level, leaving) : null;
    const title = level?.querySelector("h1") ?? null;
    if (title) title.tabIndex = -1;
    const target = control ?? field() ?? (card instanceof HTMLAnchorElement ? card : card?.querySelector<HTMLElement>("a[href]")) ?? title;
    if (target) target.focus({ preventScroll: true });
    else if (active instanceof HTMLElement) active.blur();
    return target;
  };
  const placed = place();
  const level = levelElement(stage.shown.here.id);
  const scrolled = hash === "" || (level !== null && toHash(level, hash));
  const again = () => {
    const active = document.activeElement;
    if (token !== stage.latest) return;
    const wanted = field();
    if (wanted !== null && (active === placed || active === null || active === document.body)) wanted.focus({ preventScroll: true });
    else if (!placed && (active === null || active === document.body)) place();
    if (!scrolled && level) toHash(level, hash);
  };
  stage.owed = placed && scrolled && (hash === "" || placed === field()) ? null : again;
}

export function zoom(stage: Stage, next: Route, inward: boolean, link?: Element | null, scroll = 0, hash = ""): Zoom | null {
  const palco = stageElement();
  if (!palco || reducedMotion()) return null;
  if (document.activeElement instanceof HTMLElement && palco.contains(document.activeElement)) document.activeElement.blur();
  const from = stage.shown.here;
  const to: Level = { id: ++stage.ids, route: next };
  show(stage, { here: from, other: to });
  const [leaving, arriving] = [levelElement(from.id), levelElement(to.id)];
  if (!leaving || !arriving) throw new Error("Frame did not mount a zoom's levels: it must render every one of Navigation.levels");
  const [deep, shallow] = inward ? [arriving, leaving] : [leaving, arriving];
  windows(palco, deep, shallow);
  leaving.scrollTop = scrollY;
  arriving.scrollTop = scroll;
  if (hash) toHash(arriving, hash);
  const origin = inward ? cardOf(leaving, next, link) : cardOf(arriving, from.route);
  if (!inward && origin && !visible(origin, 140)) origin.scrollIntoView({ block: "center" });
  const shot = shoot(deep, shallow, origin);
  const [start, end] = inward ? ([0, 1] as const) : ([1, 0] as const);
  shot.seek(start);
  let kept = from;
  const finish = () => {
    if (stage.flying !== flight) return;
    stage.flying = null;
    shot.cut();
    show(stage, { here: kept });
    const level = levelElement(kept.id);
    if (level) flow(palco, level);
  };
  const flight: Zoom = {
    url: next.url,
    seek(progress) {
      if (stage.flying === flight) shot.seek(start + (end - start) * progress);
    },
    land() {
      if (stage.flying !== flight || kept === to) return;
      kept = to;
      show(stage, { here: to, other: from });
      focus(stage, inward ? "zoom-in" : "zoom-out", false, from.route, hash);
      void shot.play(end).then(finish);
    },
    cancel() {
      if (stage.flying === flight) void shot.play(start).then(finish);
    },
    settle: finish,
  };
  stage.flying = flight;
  return flight;
}
